import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import { BookOpen, ShieldCheck, FileText } from "lucide-react";

const BIO =
  "Aaron Bankhurst is the founder and editor of Pennsylvania Accident Aid, where he oversees editorial standards, research, and content development for educational resources about Pennsylvania accident and injury law.";

export default function AuthorProfile() {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const schema = {
    "@context": "https://schema.org",
    "@type": "ProfilePage",
    "mainEntity": {
      "@type": "Person",
      "name": "Aaron Bankhurst",
      "url": `${origin}/authors/aaron-bankhurst`,
      "jobTitle": "Founder & Editor",
      "description": BIO,
      "worksFor": { "@type": "Organization", "name": "Pennsylvania Accident Aid", "url": origin },
    },
  };

  return (
    <>
      <SEO
        title="Aaron Bankhurst — Founder & Editor | Pennsylvania Accident Aid"
        description={BIO}
        url={`${origin}/authors/aaron-bankhurst`}
        schema={schema}
      />
      <section className="pt-12 md:pt-20 pb-16 md:pb-24 px-6 bg-bone">
        <div className="max-w-3xl mx-auto" data-testid="author-profile">
          <div className="overline mb-3">Author</div>
          <h1 className="text-4xl md:text-6xl font-display font-bold text-forest tracking-tight mb-4">
            Aaron Bankhurst
          </h1>
          <div className="text-sm font-semibold text-terracotta mb-6">Founder &amp; Editor, Pennsylvania Accident Aid</div>
          <p className="text-lg text-charcoal-soft leading-relaxed mb-10" data-testid="author-bio">{BIO}</p>

          <div className="grid sm:grid-cols-3 gap-4 mb-10">
            <Card icon={BookOpen} title="Research" sub="Topic selection and primary-source review" />
            <Card icon={ShieldCheck} title="Standards" sub="Verified legal & statistical sourcing" />
            <Card icon={FileText} title="Editing" sub="Human editorial responsibility" />
          </div>

          <div className="rounded-xl border border-black/5 bg-bone-alt p-6">
            <h2 className="text-lg font-display font-bold text-forest mb-2">How this content is produced</h2>
            <p className="text-sm text-charcoal-soft leading-relaxed mb-3">
              Every guide is grounded in Pennsylvania statutes, appellate decisions, and official data
              from sources such as PennDOT, NHTSA, and the CDC. Legal authorities and statistics are
              checked against internal verified registries before publication.
            </p>
            <div className="flex flex-wrap gap-4 text-sm font-semibold">
              <Link to="/editorial-policy" className="text-forest hover:text-terracotta transition-colors" data-testid="author-editorial-link">Editorial standards →</Link>
              <Link to="/corrections" className="text-forest hover:text-terracotta transition-colors" data-testid="author-corrections-link">Corrections policy →</Link>
            </div>
          </div>

          <div className="rounded-xl border-l-4 border-terracotta bg-terracotta/5 p-5 mt-8" data-testid="author-disclaimer">
            <p className="text-sm text-charcoal-soft leading-relaxed">
              <strong className="text-forest">Editorial disclaimer:</strong> Pennsylvania Accident Aid is an
              educational resource. It is not a law firm and does not provide legal advice, and using this
              site does not create an attorney-client relationship. Where a qualified professional has
              reviewed a specific article, that reviewer is named separately on the article.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}

function Card({ icon: Icon, title, sub }) {
  return (
    <div className="flex items-start gap-3 p-4 rounded-xl bg-white border border-black/5">
      <div className="w-9 h-9 rounded-full bg-forest text-bone grid place-items-center shrink-0"><Icon className="w-4 h-4" /></div>
      <div className="leading-tight">
        <div className="font-display font-bold text-forest">{title}</div>
        <div className="text-xs text-charcoal-soft">{sub}</div>
      </div>
    </div>
  );
}
